/**
 * Worlds Analytics Service
 *
 * Tracks user interactions with virtual tour worlds.
 * Events are saved to the worlds_analytics table in Supabase.
 * Falls back to localStorage if Supabase insert fails.
 *
 * Tracked: opens, time spent, device type, room views, likes, comments
 */

import { supabase } from '@/services/supabase'

interface AnalyticsEvent {
  event: string
  user_id?: string | null
  user_email?: string | null
  world_id?: string | null
  room_id?: string | null
  duration_seconds?: number | null
  metadata?: Record<string, unknown>
}

function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const w = window.innerWidth
  if (w < 768) return 'mobile'
  if (w < 1024) return 'tablet'
  return 'desktop'
}

function getDeviceInfo(): Record<string, unknown> {
  return {
    device: getDeviceType(),
    screen_width: window.innerWidth,
    screen_height: window.innerHeight,
    user_agent: navigator.userAgent,
    referrer: document.referrer || null,
  }
}

export async function trackWorldsEvent(
  event: string,
  data?: Omit<AnalyticsEvent, 'event'>
): Promise<void> {
  let userId: string | null = null
  let userEmail: string | null = null
  try {
    const userData = localStorage.getItem('user_data')
    if (userData) {
      const parsed = JSON.parse(userData)
      userId = parsed.id || null
      userEmail = parsed.email || null
    }
  } catch { /* ignore */ }

  const row: AnalyticsEvent = {
    event,
    user_id: data?.user_id ?? userId,
    user_email: data?.user_email ?? userEmail,
    world_id: data?.world_id ?? null,
    room_id: data?.room_id ?? null,
    duration_seconds: data?.duration_seconds ?? null,
    metadata: {
      ...getDeviceInfo(),
      ...data?.metadata,
    },
  }

  try {
    const { error } = await supabase.from('worlds_analytics').insert(row)
    if (error) {
      console.warn('[WorldsAnalytics] Supabase insert failed, storing locally:', error.message)
      storeLocally(row)
    }
  } catch {
    storeLocally(row)
  }
}

function storeLocally(row: AnalyticsEvent): void {
  try {
    const key = 'worlds_analytics_queue'
    const queue = JSON.parse(localStorage.getItem(key) || '[]')
    queue.push({ ...row, created_at: new Date().toISOString() })
    localStorage.setItem(key, JSON.stringify(queue.slice(-200)))
  } catch { /* ignore */ }
}
